import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams, useNavigate, useLocation } from "react-router-dom";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import {
  readFacilityDiscoveryReturn,
  withFacilityDiscoveryReturn,
} from "../../../common/facilityNavigation";
import { Alert, Box, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { Page } from "../../../common/components/Page";
import { CartBoundary, useCart } from "../state/cartStore";
import { useFacility } from "../hooks/useFacility";
import { useCourts } from "../hooks/useCourts";
import { useAvailability } from "../hooks/useAvailability";
import { usePricingRules } from "../hooks/usePricingRules";
import { formatMoney } from "@bw/shared/utils";
import { buildSheetModel } from "../lib/sheetModel";
import { slotEstimate, slotCurrency } from "../lib/slotEstimate";
import { venueToday, shiftDateKey, dateLabel } from "../lib/venueDate";
import { CourtSheet } from "../components/CourtSheet";
import { SheetDateNav } from "../components/SheetDateNav";
import { SheetLegend } from "../components/SheetLegend";
import { ShoppingCart } from "../components/ShoppingCart";

const DEFAULT_TZ = "America/Los_Angeles";

/**
 * L4 court-sheet container. Orchestrates facility + courts + server-resolved
 * availability + pricing into the grid model, handles loading / error / empty /
 * closed states, and composes CourtSheet + ShoppingCart + date nav.
 *
 * The CartProvider is NOT here — it is LIFTED to BookingLayout, which wraps the
 * whole /book/:facilityId subtree (sheet + review + confirmation). A provider
 * scoped to this page would leave the review screen with an empty cart (3b).
 *
 * Pure presentation + client cart state — ZERO booking logic. Availability and
 * pricing are real backend fields; the per-cell grid estimate is an estimate
 * only (3a firewall). The binding quote + charge live on the review screen (3b).
 *
 * Wrapped in CartBoundary so it shares the LIFTED cart when nested under
 * BookingLayout, but still self-provides one when rendered standalone.
 */
export function CourtSheetPage() {
  return (
    <CartBoundary>
      <CourtSheetInner />
    </CartBoundary>
  );
}

function CourtSheetInner() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const navigate = useNavigate();
  const discoveryReturn = readFacilityDiscoveryReturn(useLocation().search);
  const { toggle, has } = useCart();

  const facilityQ = useFacility(facilityId);
  // The REAL facility timezone — undefined until the facility query resolves.
  // We must NOT render time-of-day slot labels (or pick the venue "today", or
  // fetch a day's availability) using the Pacific fallback before this loads,
  // or a non-Pacific facility's slots briefly render in the wrong zone and the
  // selected day can be off by one near midnight. DEFAULT_TZ is only ever a
  // type-level fallback for the model build, which is gated off-screen until
  // the tz resolves.
  const resolvedTimezone = facilityQ.data?.timezone;
  const timezone = resolvedTimezone ?? DEFAULT_TZ;

  // Selected venue-local date (YYYY-MM-DD). Empty until the venue tz resolves,
  // then seeded to "today" in the venue tz. Keeping it empty also disables the
  // availability fetch (useAvailability is gated on a truthy date), so we never
  // request the wrong calendar day with a fallback tz.
  const [date, setDate] = useState<string>("");

  useEffect(() => {
    if (resolvedTimezone && !date) {
      setDate(venueToday(resolvedTimezone));
    }
  }, [resolvedTimezone, date]);

  const courtsQ = useCourts(facilityId);
  const availabilityQ = useAvailability(facilityId, date);
  const pricingQ = usePricingRules(facilityId);

  const estimate = useMemo(
    () => (pricingQ.data ? slotEstimate(pricingQ.data) : null),
    [pricingQ.data],
  );
  const currency = useMemo(
    () => (pricingQ.data ? slotCurrency(pricingQ.data) : "USD"),
    [pricingQ.data],
  );

  const model = useMemo(
    () =>
      buildSheetModel({
        date,
        timezone,
        courts: courtsQ.data ?? [],
        slots: availabilityQ.data ?? [],
      }),
    [date, timezone, courtsQ.data, availabilityQ.data],
  );

  const facilityName = facilityQ.data?.name ?? "this facility";

  // The window of venue-local date pills shown in the date nav. Anchored at the
  // earlier of "today" and the selected day (so stepping into the past keeps the
  // selected pill visible), and extended far enough forward that the selected day
  // is always in the window (a user who steps past the default 14-day horizon
  // still sees their day highlighted). Real date switching is unchanged — these
  // are just the pills the nav renders.
  const dateWindow = useMemo(() => {
    if (!date) return [] as string[];
    const today = resolvedTimezone ? venueToday(resolvedTimezone) : date;
    const anchor = date < today ? date : today;
    // Days from anchor up to and including the selected date, minimum 14.
    let span = 14;
    for (let i = 0; i < 400; i++) {
      if (shiftDateKey(anchor, i) === date) {
        span = Math.max(span, i + 1);
        break;
      }
    }
    return Array.from({ length: span }, (_, i) => shiftDateKey(anchor, i));
  }, [date, resolvedTimezone]);

  // The per-hour rate shown in the header, only when it is a single honest number
  // (one uniform per_hour rule → estimate). When the price is not knowable
  // client-side (>1 rule) the estimate is null and no rate is claimed.
  const rateLabel =
    estimate === null ? null : `${formatMoney(estimate, currency)}/hr`;

  // ---- Loading / error gates ------------------------------------------------
  // Gate on the venue tz being resolved (and the date seeded from it) so slot
  // labels never flash in the Pacific fallback for a non-Pacific facility.
  const isLoading =
    facilityQ.isLoading ||
    !resolvedTimezone ||
    !date ||
    courtsQ.isLoading ||
    availabilityQ.isLoading;

  const isError =
    facilityQ.isError ||
    courtsQ.isError ||
    availabilityQ.isError ||
    pricingQ.isError;

  function handleToggle(courtId: string, startTime: string) {
    const row = model.rows.find((r) => r.startTime === startTime);
    const cell = row?.cells.find((c) => c.courtId === courtId);
    // booked = inert; isAvailable is already false for past/booked slots, but
    // guard isPast explicitly so an elapsed slot can never enter the cart.
    if (!cell || !cell.slot || !cell.isAvailable || cell.isPast) return;
    const column = model.columns.find((c) => c.courtId === courtId);
    toggle({
      facility_id: facilityId as string,
      court_id: courtId,
      court_name: column?.name ?? "Court",
      start_time: cell.slot.start_time,
      end_time: cell.slot.end_time,
      estimate,
      currency,
    });
  }

  function isSelected(courtId: string, startTime: string) {
    return has({ court_id: courtId, start_time: startTime });
  }

  // ---- Render ---------------------------------------------------------------
  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Stack
        direction="row"
        spacing={1.5}
        role="status"
        aria-live="polite"
        sx={{ py: 4, alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress size={22} />
        <Typography color="text.secondary">
          Loading court availability…
        </Typography>
      </Stack>
    );
  } else if (isError) {
    body = (
      <Alert severity="error" role="alert">
        Something went wrong loading availability. Please try again.
      </Alert>
    );
  } else if (model.isClosed) {
    body = (
      <Alert severity="info">
        No hours available — {facilityName} is closed on this day.
      </Alert>
    );
  } else {
    const openCount = model.rows.reduce(
      (n, r) => n + r.cells.filter((c) => c.isAvailable).length,
      0,
    );
    if (openCount === 0) {
      body = (
        <Alert severity="info">
          No open times available for this day. Try another date.
        </Alert>
      );
    } else {
      body = (
        <Stack spacing={1.5}>
          <CourtSheet
            model={model}
            estimate={estimate}
            currency={currency}
            isSelected={isSelected}
            onToggleSlot={handleToggle}
          />
          <SheetLegend />
        </Stack>
      );
    }
  }

  return (
    <Page>
      <Box
        data-testid="booking-layout"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1fr) 320px",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Stack
          data-testid="booking-main"
          spacing={1.5}
          sx={{
            minWidth: 0,
            "@media (max-width: 720px)": {
              pb: "calc(64px + env(safe-area-inset-bottom, 0px) + 80px)",
            },
          }}
        >
          {/* Booking header: the venue name, then the selected venue-local
              day and the honest per-hour rate (only when it is a single knowable
              number). The app bar owns the app title, so this is the one page
              heading. */}
          <Box data-testid="booking-header" sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <IconButton
              component={RouterLink}
              to={withFacilityDiscoveryReturn(`/facility/${facilityId}`, discoveryReturn)}
              aria-label="Back to facility"
              edge="start"
            >
              <ArrowBackRounded />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
              {facilityQ.data?.name ?? "Reserve courts"}
            </Typography>
            {date ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {dateLabel(date)}
                {rateLabel ? (
                  <>
                    <Box component="span" aria-hidden="true" sx={{ mx: 0.75 }}>
                      ·
                    </Box>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {rateLabel}
                    </Box>
                  </>
                ) : null}
              </Typography>
            ) : null}
            </Box>
          </Box>

          {/* Only show the date pills once the venue date has resolved — an empty
              date key formats as "Invalid Date" and there is nothing to step. */}
          {date ? (
            <SheetDateNav
              date={date}
              days={dateWindow}
              timezoneLabel={resolvedTimezone ? "venue local" : null}
              onPrev={() => setDate((d) => shiftDateKey(d, -1))}
              onNext={() => setDate((d) => shiftDateKey(d, 1))}
              onSelect={(d) => setDate(d)}
            />
          ) : null}

          {body}
        </Stack>

        <Box
          data-testid="booking-cart"
          sx={{ position: { lg: "sticky" }, top: { lg: 88 } }}
        >
          {/* ShoppingCart chooses its own presentation by viewport: the static
              right-rail "YOUR CART" panel on desktop (lives in this column, never
              overlaps the grid), or an ultra-minimal fixed bottom bar + opt-in
              detail sheet on phones. It owns its own wrappers, so the page just
              mounts it here; when the cart is empty the mobile bar renders
              nothing (no fake empty bar). */}
          <ShoppingCart
            timezone={timezone}
            currency={currency}
            onReview={() =>
              navigate(
                withFacilityDiscoveryReturn(
                  `/book/${facilityId}/review`,
                  discoveryReturn,
                ),
              )
            }
          />
        </Box>
      </Box>
    </Page>
  );
}
